import type { Tower } from "../entities/Tower.ts";
import { COLORS } from "../core/constants.ts";

/**
 * Active skill visual modifiers — set externally per frame.
 */
export interface TowerVisualEffects {
  /** Overclock / Hyper Threading: fire rate buff active */
  fireRateBoosted: boolean;
  /** Cache Hit: range boosted */
  rangeBoosted: boolean;
  /** Quantum Surge: damage doubled */
  damageBoosted: boolean;
  /** Element-specific damage buff (overload cards) */
  elementBoosted: Record<string, boolean>;
  /** Piercing shots active */
  piercingActive: boolean;
  /** Time Warp is currently active */
  timeWarpActive: boolean;
  /** Recursive Loop armed */
  recursiveLoopActive: boolean;
}

export const DEFAULT_VISUAL_EFFECTS: TowerVisualEffects = {
  fireRateBoosted: false,
  rangeBoosted: false,
  damageBoosted: false,
  elementBoosted: {},
  piercingActive: false,
  timeWarpActive: false,
  recursiveLoopActive: false,
};

/**
 * Renders active draft skill visual overlays on towers.
 */
export function drawSkillEffects(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
  time: number,
  fx: TowerVisualEffects,
): void {
  const t = time;

  // ─── OVERCLOCK / HYPER THREADING: spinning speed rings ───
  if (fx.fireRateBoosted) {
    const spinSpeed = fx.damageBoosted ? 8 : 5;
    ctx.strokeStyle = COLORS.AMBER_WARN;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.4 + Math.sin(t * 10) * 0.2;
    
    // shadowBlur removed (M5-F2)
    const r = size * 0.46;
    for (let i = 0; i < 3; i++) {
      const startAngle = t * spinSpeed + (i * Math.PI * 2) / 3;
      ctx.beginPath();
      ctx.arc(cx, cy, r, startAngle, startAngle + 0.8);
      ctx.stroke();
    }
    // shadowBlur removed (M5-F2)
    ctx.globalAlpha = 1;
  }

  // ─── CACHE HIT: expanded range halo ───
  if (fx.rangeBoosted) {
    const rangeR = size * 0.55;
    ctx.strokeStyle = COLORS.ICE_CYAN;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.2 + Math.sin(t * 3) * 0.1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(cx, cy, rangeR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  // ─── QUANTUM SURGE: intense damage aura ───
  if (fx.damageBoosted) {
    const surgeR = size * 0.38;
    const flash = 0.3 + Math.abs(Math.sin(t * 6)) * 0.4;
    ctx.fillStyle = `rgba(255, 50, 50, ${flash * 0.15})`;
    
    // shadowBlur removed (M5-F2)
    ctx.beginPath();
    ctx.arc(cx, cy, surgeR, 0, Math.PI * 2);
    ctx.fill();
    // shadowBlur removed (M5-F2)

    // X-cross energy marks
    ctx.strokeStyle = COLORS.MALWARE_RED;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = flash;
    const markR = size * 0.2;
    ctx.beginPath();
    ctx.moveTo(cx - markR, cy - markR);
    ctx.lineTo(cx + markR, cy + markR);
    ctx.moveTo(cx + markR, cy - markR);
    ctx.lineTo(cx - markR, cy + markR);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // ─── ELEMENT OVERLOAD: boosted element glow ring ───
  if (fx.elementBoosted[tower.element]) {
    const olR = size * 0.42;
    const olAlpha = 0.3 + Math.sin(t * 4 + cx * 0.1) * 0.15;
    ctx.strokeStyle = tower.color;
    ctx.lineWidth = 2.5;
    ctx.globalAlpha = olAlpha;
    
    // shadowBlur removed (M5-F2)
    ctx.beginPath();
    ctx.arc(cx, cy, olR, 0, Math.PI * 2);
    ctx.stroke();
    // shadowBlur removed (M5-F2)
    ctx.globalAlpha = 1;
  }

  // ─── PIERCING SHOTS: arrow indicators ───
  if (fx.piercingActive && tower.element !== "lightning") {
    ctx.fillStyle = "#FFFFFF";
    ctx.globalAlpha = 0.5 + Math.sin(t * 5) * 0.2;
    const arrSize = 3;
    const arrY = cy - size * 0.35;
    ctx.beginPath();
    ctx.moveTo(cx - arrSize, arrY + arrSize);
    ctx.lineTo(cx, arrY - arrSize);
    ctx.lineTo(cx + arrSize, arrY + arrSize);
    ctx.moveTo(cx - arrSize, arrY + arrSize * 2.5);
    ctx.lineTo(cx, arrY + arrSize * 0.5);
    ctx.lineTo(cx + arrSize, arrY + arrSize * 2.5);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // ─── TIME WARP ACTIVE: frozen time rings ───
  if (fx.timeWarpActive) {
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.6;
    
    // shadowBlur removed (M5-F2)
    const warpR = size * 0.48;
    ctx.beginPath();
    ctx.arc(cx, cy, warpR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, warpR * 0.7, 0, Math.PI * 2);
    ctx.stroke();
    // shadowBlur removed (M5-F2)
    ctx.globalAlpha = 1;
  }
}

/**
 * Renders the lightning beam tower's laser effect with skill-aware modifiers.
 */
export function drawLaserBeam(
  ctx: CanvasRenderingContext2D,
  tower: Tower,
  time: number,
  fx: TowerVisualEffects,
): void {
  const target = tower.beamTarget!;
  const t = time;
  const baseBCount = Math.min(tower.level, 3);
  const beamCount = fx.fireRateBoosted ? baseBCount + 1 : baseBCount;
  const baseWidth = fx.damageBoosted ? 4.0 : 2.5;
  const beamColor = fx.damageBoosted ? "#FF6666" : tower.color;

  for (let b = 0; b < beamCount; b++) {
    const offset = b === 0 ? 0 : b % 2 === 1 ? 2 : -2;

    ctx.strokeStyle = beamColor;
    ctx.lineWidth = baseWidth - b * 0.5;
    
    // shadowBlur removed (M5-F2)
    ctx.globalAlpha = 0.7 + Math.sin(t * 20 + b) * 0.3;
    ctx.beginPath();
    ctx.moveTo(tower.x + offset, tower.y + offset);
    ctx.lineTo(target.x + offset, target.y + offset);
    ctx.stroke();

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    // shadowBlur removed (M5-F2)
    ctx.beginPath();
    ctx.moveTo(tower.x + offset, tower.y + offset);
    ctx.lineTo(target.x + offset, target.y + offset);
    ctx.stroke();
  }

  ctx.globalAlpha = 1.0;
  // shadowBlur removed (M5-F2)

  const impactSize = fx.damageBoosted ? 6 : 4;
  ctx.fillStyle = beamColor;
  
  // shadowBlur removed (M5-F2)
  ctx.beginPath();
  ctx.arc(
    target.x,
    target.y,
    impactSize + Math.sin(t * 15) * 2,
    0,
    Math.PI * 2,
  );
  ctx.fill();
  // shadowBlur removed (M5-F2)
}

export function drawUpgradeIndicator(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  tower: Tower,
): void {
  const level = tower.level - 1;
  const pipSize = 3;
  const gap = 2;
  const totalWidth = level * pipSize + (level - 1) * gap;
  const startX = cx - totalWidth / 2;
  const y = cy + size * 0.38;

  for (let i = 0; i < level; i++) {
    const x = startX + i * (pipSize + gap);
    ctx.fillStyle = tower.level >= 5 ? "#ffaa00" : COLORS.SENTINEL_GREEN;
    
    // shadowBlur removed (M5-F2)
    ctx.fillRect(x, y, pipSize, pipSize);
  }
  // shadowBlur removed (M5-F2)
}
